import { useMemo, type ReactElement } from 'react'

import { useDailyGiftWidget } from 'containers/WidgetsBar/hooks/useDailyGiftWidget'
import { useStashWidget } from 'containers/WidgetsBar/hooks/useStashWidget'
import WidgetComponent from 'src/components/WidgetComponent'
import { useAppSelector } from 'src/store/hooks'
import { getAdminOnlyWidgets, getWidgetsConfig } from 'src/store/modules/brandConfig/selectors'
import { getIsAdminUserSelector } from 'src/store/modules/user/selectors'

import HorizontalScroll from '../HorizontalScroll'

import { WidgetsVariant } from './hooks/types'
import { useOfferWidget } from './hooks/useOfferWidget'
import { usePromoWidget } from './hooks/usePromoWidget'
import { useReferFriendWidget } from './hooks/useReferFriendWidget'
import classes from './styles.module.scss'
import { parseWidgetsConfig } from './utils/parseWidgetsConfig'

function WidgetsBar() {
  const offerWidget = useOfferWidget()
  const referFriendWidget = useReferFriendWidget()
  const promoWidget = usePromoWidget()
  const stashWidget = useStashWidget()
  const dailyGiftWidget = useDailyGiftWidget()
  const widgetsConfig = useAppSelector(getWidgetsConfig)
  const adminOnlyWidgets = useAppSelector(getAdminOnlyWidgets)
  const isAdmin = useAppSelector(getIsAdminUserSelector)
  const widgetsOrderArray = useMemo(
    () => parseWidgetsConfig(widgetsConfig, adminOnlyWidgets, isAdmin),
    [widgetsConfig, adminOnlyWidgets, isAdmin],
  )
  const widgetComponents = useMemo<Record<WidgetsVariant, ReactElement | null>>(
    () => ({
      [WidgetsVariant.OFFER]: offerWidget.isWidgetVisible ? (
        <WidgetComponent key={WidgetsVariant.OFFER} {...offerWidget} />
      ) : null,
      [WidgetsVariant.DAILY_GIFT]: dailyGiftWidget.isWidgetVisible ? (
        <WidgetComponent key={WidgetsVariant.DAILY_GIFT} {...dailyGiftWidget} />
      ) : null,
      [WidgetsVariant.STASH]: stashWidget.isWidgetVisible ? (
        <WidgetComponent key={WidgetsVariant.STASH} {...stashWidget} />
      ) : null,
      [WidgetsVariant.REFER_A_FRIEND]: referFriendWidget.isWidgetVisible ? (
        <WidgetComponent key={WidgetsVariant.REFER_A_FRIEND} {...referFriendWidget} />
      ) : null,
      [WidgetsVariant.PROMO]: promoWidget.isWidgetVisible ? (
        <WidgetComponent key={WidgetsVariant.PROMO} {...promoWidget} />
      ) : null,
    }),
    [offerWidget, dailyGiftWidget, stashWidget, referFriendWidget, promoWidget],
  )
  const renderedWidgets = useMemo(
    () =>
      widgetsOrderArray
        .map((variant) => widgetComponents[variant])
        .filter((widget): widget is ReactElement => Boolean(widget)),
    [widgetsOrderArray, widgetComponents],
  )

  if (!widgetsOrderArray.length) {
    return null
  }

  return (
    <HorizontalScroll data-test='widgets-bar' classNameContainer={classes.root} linesAmount={0.5}>
      <div className={classes.widgetsContainer}>{renderedWidgets}</div>
    </HorizontalScroll>
  )
}

export default WidgetsBar
